import { useCallback, useRef, useState } from 'react';
import type { DownloadResult } from '../api/client';
import { useExportCsv, useExportPdf } from '../api/report-queries';
import { ApiError } from '../api/errors';

/**
 * Export actions shared by the editor bar and the report list's row export.
 *
 * One status covers both PDF and CSV, because only one export can be in
 * flight from a given trigger at a time and the caller never needs to tell
 * them apart on screen — the button that started the request already knows
 * which one it was.
 */
export type ExportStatus =
  | { kind: 'idle' }
  | { kind: 'working' }
  | { kind: 'done'; message: string }
  | { kind: 'error'; message: string };

/** `EXPORT_FAILED` means the server could not render the PDF — never the
 *  caller's fault, so this names the one thing they can still do (spec §10.2). */
export const PDF_FAILED_MESSAGE = "The PDF couldn't be created. Try again, or download the CSV.";

/** How long the object URL stays alive before `saveToDisk` revokes it. */
const REVOKE_DELAY_MS = 10_000;

/** Hands the file to the browser. An object URL rather than a navigation, so a
 *  failed export never replaces the workspace with a JSON error page. */
export function saveToDisk(result: DownloadResult): void {
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = result.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next turn: revoking synchronously races the download start
  // in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
}

export type ExportCsvScope = { kind: 'dataset' } | { kind: 'table'; widgetId: string };

export interface UseExportOptions {
  /** Overrides the real `saveToDisk`. Tests only. */
  saveToDisk?: (result: DownloadResult) => void;
}

export interface UseExportResult {
  status: ExportStatus;
  exportPdf(): Promise<void>;
  exportCsv(scope: ExportCsvScope): Promise<void>;
}

function describeFailure(cause: unknown): string {
  if (ApiError.isApiError(cause)) {
    return cause.code === 'EXPORT_FAILED' ? PDF_FAILED_MESSAGE : cause.message;
  }
  return cause instanceof Error ? cause.message : String(cause);
}

export function useExport(reportId: string, options: UseExportOptions = {}): UseExportResult {
  const [status, setStatus] = useState<ExportStatus>({ kind: 'idle' });
  const pdf = useExportPdf().mutateAsync;
  const csv = useExportCsv().mutateAsync;

  // A ref, so a stable `run` never has to be rebuilt when the caller passes a
  // fresh options object on every render.
  const saveRef = useRef(options.saveToDisk ?? saveToDisk);
  saveRef.current = options.saveToDisk ?? saveToDisk;

  // Guards against a slow export finishing after a faster one that started
  // later: only the most recently started run is allowed to touch status or
  // save a file. Without this, a stale PDF response that resolves after a
  // quick CSV export would overwrite the newer, already-settled status.
  const requestIdRef = useRef(0);

  const run = useCallback(async (fetchDownload: () => Promise<DownloadResult>): Promise<void> => {
    const requestId = (requestIdRef.current += 1);
    setStatus({ kind: 'working' });
    try {
      const result = await fetchDownload();
      if (requestIdRef.current !== requestId) return;
      saveRef.current(result);
      setStatus({ kind: 'done', message: `Downloaded ${result.filename}` });
    } catch (cause) {
      if (requestIdRef.current !== requestId) return;
      setStatus({ kind: 'error', message: describeFailure(cause) });
    }
  }, []);

  const exportPdf = useCallback(() => run(() => pdf({ reportId })), [reportId, pdf, run]);

  const exportCsv = useCallback(
    (scope: ExportCsvScope) => run(() => csv({ reportId, scope })),
    [reportId, csv, run]
  );

  return { status, exportPdf, exportCsv };
}
